import { useEffect } from 'react';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useFocusOnMount } from '../../utilities/customHooks/useFocusOnMount';

import { ButtonLink } from '../../core-components/theme/components/Buttons';
import { EmptyState } from '../../core-components/theme/components/EmptyState';
import { trackEvent } from '../../modules/analytics';
import { ACCESS_DENIED } from '../../modules/analytics/events';
import type { DenyReason } from '../domain/decide';
import { HOME_PATH } from '../paths';

const NO_ACCESS_ICON = <LockOutlinedIcon />;

const rootSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 4, paddingBlock: 4 };

export const descriptionOf = (t: TFunction, reason: DenyReason, atPlant: boolean): string => {
  switch (reason) {
    case 'NO_ASSIGNMENT':
      return atPlant
        ? t('access.noAccess.notAssigned', 'You aren’t assigned to this plant.')
        : t('access.noAccess.noPlants', 'You aren’t assigned to any plant yet. Ask your site admin to add you.');
    case 'MODULE_UNLICENSED':
      return t('access.noAccess.unlicensed', 'This plant’s subscription doesn’t include this feature.');
    case 'NO_PLANT_CONTEXT':
      return t('access.noAccess.noPlant', 'Open this page from one of your plants.');
    case 'NOT_GRANTED':
    case 'NO_ADMIN_GRANT':
    case 'OUT_OF_CLUSTER':
    case 'AUDIENCE':
    case 'UNDECLARED':
      return t('access.noAccess.notGranted', 'Your access doesn’t include this page. Ask your site admin if you need it.');
  }
};

interface NoAccessScreenProps {
  readonly reason: DenyReason;
  readonly atPlant?: boolean;
}

export const NoAccessScreen = ({ reason, atPlant = false }: NoAccessScreenProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const titleRef = useFocusOnMount<HTMLElement>(true);

  useEffect(() => {
    trackEvent(ACCESS_DENIED, { reason, path: pathname });
  }, [reason, pathname]);

  return (
    <Box sx={rootSx}>
      <EmptyState
        align="start"
        headingLevel="h1"
        titleRef={titleRef}
        icon={NO_ACCESS_ICON}
        title={t('access.noAccess.title', 'You can’t open this page')}
        description={descriptionOf(t, reason, atPlant)}
        action={
          <ButtonLink intent="primary" to={HOME_PATH}>
            {t('access.noAccess.home', 'Go to my home page')}
          </ButtonLink>
        }
      />
    </Box>
  );
};
